<template>
  <div class="nx-form-generator-wrapper">
    <v-layout row wrap fill-heigth>
      <!--Form with scope tabs -->
      <v-flex sm8 v-if="showTabs" class="nx-form-generator-tabs">
        <nx-form-tabs
          ref="tabs"
          :id="affixTabs ? 'nx-form-generator-tabs' : ''"
          :class="{'collapse': $vuetify.breakpoint.mdAndDown, 'nx-form-generator-tabs': true }"
          :tabs="tabs"
          :advanced="this.basicAdvancedEnabled"
          :fields="fields || {}"
          :errors="errors.items"
          :warnings="computedWarnings"
          @validate="validateScope"
          @toggleAdvancedMode="toggleAdvancedMode()"
          v-show="tabs.length > 0"
          :visible-scope="visibleScope"
        />
      </v-flex>

      <v-flex xs12 offset-sm1 sm11 offset-lg3 lg9 class="nx-form-generator-form">
        <nx-form-scope-card
          v-for="(scope, index) in visibleScopesConfig"
          :key="index"
          :starts-collapsed="scopeIsCollapsed(scope.title)"
          :title="$t(`message.form.scopes.${scope.title}`)"
          :icon="scope.icon"
          :fields="scopeFields(scope.title) || {}"
          :errors="scopeErrors(scope.title) || []"
          :warnings="scopeWarnings(scope.title) || []"
          :scope="scope.title"
          collapsable
          @collapse="onCollapse(scope.title, $event)"
          @visible="onVisibleScope(scope.title)"
          @invisible="onInvisibleScope(scope.title)"
        >
          <component
            :index="index"
            :config="scope"
            :disabled="disabled"
            :entityId="entityId"
            :entity="entity"
            :editor-fields-config="editorFieldsConfig"
            :is="getScopeComponent(scope)"
            v-bind="scope.params"
            :key="scope.title"
            :scope="scope.scope || scope.title"
            :title="scope.title"
            :value="formValue"
            @input="updateField"
            @trigger-population="$emit('trigger-population', $event)"
            @bcube-assistant="openBcubeAssistant"
          ></component>
        </nx-form-scope-card>
      </v-flex>
    </v-layout>
    <nx-form-error-dialog
      :visible="errorDialog"
      @close="errorDialog = false"
      :scopes="dialogTableScopes"
      :title="$t('message.content.buttons.dialogTitle')"
      icon="error_outline"
    />
    <nx-dialog-confirm
      :text="$t('message.content.editor.saveFormConfirmText')"
      :title="$t('message.content.editor.saveFormConfirmTitle')"
      accept-color="success"
      cancel-color="error"
      ref="confirm"
    />
    <nx-fullscreen-dialog
      :title="bcubeAssistantTitle"
      :active="showBcubeAssistant"
      icon="tips_and_updates"
      @dismiss="closeBcubeAssistant"
    >
      <component
        v-if="bcubeAssistantData"
        :is="bcubeAssistantData.component"
        :input-name="bcubeAssistantData.inputName"
        :parent-editor="$getParentEditor()"
        @dismiss="closeBcubeAssistant"
      ></component>
    </nx-fullscreen-dialog>
  </div>
</template>

<script>
import Form from '@/modules/form/Form';
import ShowDisableConditionally from '@/modules/form/mixins/show-disable-conditionally';
import PrepareFieldsConfig from '@/modules/form/mixins/prepare-fields-config';
import FormScopeGenerator from './FormScopeGenerator';
import NxFormErrorDialog from '@/modules/form/components/FormErrorDialog';
import NxFormTabs from '@/modules/form/components/FormTabs';
import NxFormScopeCard from '@/modules/form/components/FormScopeCard';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import {
  cloneDeep, isUndefined, isNumber, isObject,
} from 'lodash';
import store from '@/store';
import UserEditorConfig from '@/modules/form/mixins/user-editor-config';
import Storable from '@/mixins/storable';
import { mapGetters } from 'vuex';

import { operatorToString } from '@/utils';
import NxFullscreenDialog from '@/components/dialogs/FullscreenDialog';

/**
 * Form Generator
 *
 * Config example:
 *
 * With Fields Config
 * [
 *  {
 *    icon: 'sort',
 *    scope: 'content',
 *    title: 'Datos básicos',
 *    fieldsConfig: [
 *      {
 *        field: 'name',
 *        label: 'Nombre del autor',
 *        component: 'NxFormText',
 *        params: {
 *          required: true,
 *        },
 *      },
 *      {
 *        field: 'nick',
 *        label: 'Nick',
 *        component: 'NxFormText',
 *        params: {
 *          required: true,
 *          min: 4,
 *        },
 *      },
 *    ],
 *  },
 * ]
 *
 * With Component based config
 * [
 *  {
 *    title: 'content',
 *    icon: 'sort',
 *    component: ContentMobile,
 *  },
 *  {
 *    title: 'multimedia',
 *    icon: 'add_a_photo',
 *    component: Multimedia,
 *  },
 * ]
 */
export default {
  name: 'nx-form-generator',
  extends: Form,
  inject: {
    $getParentEditor: {
      default: null,
    },
  },
  components: {
    NxFullscreenDialog,
    NxFormTabs,
    NxFormErrorDialog,
    NxFormScopeCard,
    NxDialogConfirm,
  },
  mixins: [
    ShowDisableConditionally,
    PrepareFieldsConfig,
    Storable,
    UserEditorConfig,
  ],
  provide() {
    return {
      // Ojo, esto no es reactivo: quiere decir que una vez se ha cargado un formulario, si se accede
      // al formulario de otra entidad, el $entityId que se estará inyectando pertenece al del primer form
      // esto puede afectar a componentes o mixins que injecten esto, ej: HasEntityFields, TaxonomyFormSelector, etc
      $entityId: this.entityId,
      $entityRelativeVersionId: this.entityRelativeVersionId,
    };
  },
  props: {
    basicAdvancedEnabled: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    editorFieldsConfig: {
      type: Object,
      default: () => ({}),
    },
    entityId: {
      type: Number,
    },
    entity: {
      type: String,
    },
    entityRelativeVersionId: {
      type: Number,
    },
    formScopeComponent: {
      required: false,
    },
    showTabs: {
      type: Boolean,
    },
    formValue: {
      type: Object,
      required: true,
    },
    affixTabs: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      errorDialog: false,
      scopesToBeShown: [],
      visibleScope: null,
      scopesOnViewport: [],
      bcubeAssistantData: null,
    };
  },
  computed: {
    ...mapGetters([
      'features',
    ]),
    showBcubeAssistant() {
      return this.bcubeAssistantData !== null;
    },
    bcubeAssistantTitle() {
      if (this.bcubeAssistantData === null) {
        return 'Bcube Assistant';
      }

      if (!this.bcubeAssistantData.inputName) {
        return 'Bcube Assistant';
      }

      return `Bcube Assistant: ${this.$t(`message.form.fields.${this.bcubeAssistantData.inputName}`)}`;
    },
    storePath() {
      return this.$getParentEditor().storeName;
    },
    /** @returns {Array} */
    visibleScopes() {
      return this.config
        .filter(this.isVisible)
        .map(scopeConfig => scopeConfig.scope);
    },
    /** @returns {Array} */
    visibleScopesConfig() {
      return cloneDeep(this.config)
        .filter(scopeConfig => this.scopesToBeShown.includes(scopeConfig.scope))
        .filter((scopeConfig) => {
          // advanced -> show everything as it is
          if (this.$getParentEditor().state.advancedMode || !this.basicAdvancedEnabled) {
            return scopeConfig;
          }

          // basic -> filter fields and show if has any
          scopeConfig.fieldsConfig = scopeConfig.fieldsConfig.filter(fc => fc.showInBasicEditor).map((fc) => {
            if (fc.component.startsWith('Layout')) {
              fc.params.fieldsConfigFirstColumn = fc.params.fieldsConfigFirstColumn
                .filter(childFc => childFc.showInBasicEditor);
              fc.params.fieldsConfigSecondColumn = fc.params.fieldsConfigSecondColumn
                .filter(childFc => childFc.showInBasicEditor);
              if (fc.component === 'LayoutThreeColumns') {
                fc.params.fieldsConfigThirdColumn = fc.params.fieldsConfigThirdColumn
                  .filter(childFc => childFc.showInBasicEditor);
              }
              if (fc.component === 'LayoutFourColumns') {
                fc.params.fieldsConfigFourthColumn = fc.params.fieldsConfigFourthColumn
                  .filter(childFc => childFc.showInBasicEditor);
              }
              return fc;
            }
            return fc;
          });

          return scopeConfig;
        });
    },
    dialogTableScopes() {
      return Object
        .entries(this.formErrorsByScope)
        .map(([scope, errors]) => ({ scope: this.getScopeTitle(scope), items: errors }));
    },
    /**
     * Get form scope tabs info
     * @returns {Array}
     */
    tabs() {
      return this.visibleScopesConfig
        .filter(config => config.tab !== false)
        .map((config) => {
          const scope = config.scope;
          const title = config.title;
          const icon = config.icon;
          const fields = this.getScopeFields(scope);

          return {
            title,
            icon,
            scope,
            errors: this.getScopeErrors(scope),
            fields,
          };
        });
    },
    computedWarnings() {
      const isWarning = field => this.fields[field].recommended
        || (this.fields[field].recommendedNotEmpty && this.fields[field].empty);
      return Object.keys(this.fields).filter(isWarning);
    },
  },
  methods: {
    openBcubeAssistant(event) {
      this.bcubeAssistantData = event;
    },
    closeBcubeAssistant() {
      this.bcubeAssistantData = null;
    },
    scopeErrors(scopeTitle) {
      return this.errors.items.filter(e => e.scope === scopeTitle);
    },
    scopeWarnings(scopeTitle) {
      return this.computedWarnings.filter(e => e.scope === scopeTitle);
    },
    scopeFields(scopeTitle) {
      return this.fields[`$${scopeTitle}`];
    },
    async saveChanges() {
      await store.dispatch('UPDATE_LOADING_VIEW', true);
      try {
        await this.dispatch('SAVE');
        this.resetFields();
        // eslint-disable-next-line no-empty
      } catch { }
    },
    async toggleAdvancedMode() {
      // From basic to advanced there's no need to save anything, all fields will be visible, we only have to check
      // the other way around
      if (this.$getParentEditor().state.advancedMode) {
        if (!await this.validateAndShowErrors()) {
          return;
        }

        if (this.form.dirty) {
          if (this.features.AUTOSAVE_ADVANCED_EDITOR) {
            await this.saveChanges();
          } else {
            const agree = await this.$refs.confirm.open();
            if (agree) {
              await this.saveChanges();
            } else {
              return;
            }
          }
        }
      }

      await store.dispatch('UPDATE_LOADING_VIEW', true);
      await this.dispatch('TOGGLE_ADVANCED_EDITOR');
      this.saveAdvancedModeConfig(this.$getParentEditor().state.advancedMode);
      await this.updateScopesToBeShown();
      await store.dispatch('UPDATE_LOADING_VIEW', false);
    },
    updateScopesToBeShown() {
      this.scopesToBeShown = cloneDeep(this.config)
        .filter(this.isVisible)
        .filter(c => this.filterByAdvancedMode(c))
        .map(scopeConfig => scopeConfig.scope);
    },
    filterByAdvancedMode(scopeConfig) {
      const advancedMode = this.$getParentEditor().state.advancedMode;

      if (advancedMode || !this.basicAdvancedEnabled) {
        return true;
      }

      let visibleCount = 0;

      scopeConfig.fieldsConfig.forEach((fc) => {
        if (fc.showInBasicEditor) {
          visibleCount += 1;
        }
      });

      return visibleCount > 0;
    },
    onCollapse(scopeName, collapsed) {
      this.saveScopeConfig(scopeName, collapsed);
    },
    configureAffixTabs() {
      if (!this.affixTabs) {
        return;
      }
      const tabs = document.getElementById('nx-form-generator-tabs');
      const menu = document.getElementById('nx-editor-header');
      if (!menu || !tabs) return;
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'style') {
            tabs.style.transform = menu.style.transform;
          }
        });
      });
      observer.observe(menu, { attributes: true, attributeFilter: ['style'] });
    },
    async validateAndShowErrors() {
      this.errors.clear();
      const isValid = await this.$validator.validate();
      // If form is not valid, open the error dialog
      this.errorDialog = !isValid;
      return isValid;
    },
    async validateForPublishAndShowErrors() {
      this.errors.clear();
      await this.$validator.validate();
      const isValidForPublish = this.form.validForPublish;
      const isValid = this.form.valid;

      const extractScopeAndFieldNames = (definition) => {
        const regex = /\$(\w+)\/(\w+)/;
        if (regex.test(definition)) {
          const [, scope, field] = definition.match(regex);
          return {
            scope, field,
          };
        }
        return {
          scope: null,
          field: definition,
        };
      };

      const translateScopeField = (scope, field) => {
        const fieldConfig = this.getScopeFieldConfig(scope, field);
        const fieldName = fieldConfig ? fieldConfig.label || fieldConfig.field : field;
        const i18n = `message.form.fields.${fieldName}`;
        return this.$te(i18n) ? this.$t(i18n) : fieldName;
      };

      if (!isValidForPublish) {
        const invalidForPublishFields = Object
          .entries(this.formFields)
          .filter(([formField, formFieldFlags]) => formFieldFlags.requiredForPublish === true)
          .filter(([formField, formFieldFlags]) => formFieldFlags.validForPublish === false)
          .map(([formField, formFieldFlags]) => formField);

        invalidForPublishFields.forEach((scopedField) => {
          const { field, scope } = extractScopeAndFieldNames(scopedField);
          let msg = this.$t('message.error.requiredForPublish', { field: translateScopeField(scope, field) });
          // Search for custom required for publish message
          const fieldConfig = this.getScopeFieldConfig(scope, field);
          if (fieldConfig !== null
            && fieldConfig.params !== undefined
            && fieldConfig.params.requiredForPublishMessage !== undefined
            && fieldConfig.params.requiredForPublishMessage !== '') {
            msg = this.$t(`message.error.requiredForPublishCustom.${fieldConfig.params.requiredForPublishMessage}`, { field: translateScopeField(scope, field) });
          }
          this.errors.add({
            field,
            scope,
            msg,
          });
        });

        const invalidValueForPublishFields = Object
          .entries(this.formFields)
          .filter(([formField, formFieldFlags]) => formFieldFlags.requiredValueForPublish !== null)
          .filter(([formField, formFieldFlags]) => formFieldFlags.validForPublish === false)
          .map(([formField, formFieldFlags]) => ({
            field: formField,
            value: this.getValueForPublish(formFieldFlags.requiredValueForPublish),
            operator: formFieldFlags.requiredValueForPublish.operator,
            objectKey: formFieldFlags.requiredValueForPublish.objectKey,
          }));

        invalidValueForPublishFields.forEach((fieldObj) => {
          const { field, scope } = extractScopeAndFieldNames(fieldObj.field);
          let errorI18n = 'message.error.requiredValueForPublish';
          if (!isUndefined(fieldObj.operator)) {
            errorI18n = 'message.error.requiredValueForPublishOperator';
            if (!isUndefined(fieldObj.objectKey)
              && isNumber(fieldObj.value)) {
              errorI18n = `message.error.requiredValueForPublishOperatorKeyAndNumericValue.${fieldObj.value}`;
            }
            // If is a numeric array:
            if (!isUndefined(fieldObj.objectKey)
              && Array.isArray(fieldObj.value)
              && fieldObj.value.length > 0
              && fieldObj.value.every(element => isNumber(element))
            ) {
              errorI18n = `message.error.requiredValueForPublishOperatorKeyAndNumericValue.${fieldObj.value}`;
            }
          }
          this.errors.add({
            field,
            scope: scope || fieldObj.scope,
            msg: this.$t(
              errorI18n,
              {
                field: translateScopeField(scope, field),
                value: fieldObj.value,
                operator: fieldObj.operator ? this.$t(`message.operator.${operatorToString(fieldObj.operator)}`) : null,
              },
            ),
          });
        });
      }
      this.errorDialog = !isValid || !isValidForPublish;
      return isValid && isValidForPublish;
    },
    getScopeComponent(scope) {
      if (scope.component) {
        return scope.component;
      }

      // Forced form scope component
      if (this.formScopeComponent) {
        return this.formScopeComponent;
      }

      // Return the Generator Scope by default;
      return FormScopeGenerator;
    },
    getScopeTitle(scope) {
      const regex = /.+\/[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89aAbB][a-f0-9]{3}-[a-f0-9]{12}/;
      const isRelationScope = scope.match(regex);
      if (isRelationScope) {
        const field = scope.split('/')[0];
        return this.$t(`message.form.fields.${field}`);
      }
      const config = this.visibleScopesConfig.find(el => el.scope === scope);
      if (!config) {
        return scope;
      }
      if (config.title) {
        return this.$t(`message.form.scopes.${config.title}`);
      }
      return this.$t(`message.form.scopes.${config.scope}`);
    },
    getScopeFieldConfig(scopeName, fieldName) {
      if (!scopeName || !fieldName) {
        return null;
      }
      const scopeConfig = this.config.find(({ scope }) => scope === scopeName);
      if (!scopeConfig) {
        return null;
      }
      return scopeConfig.fieldsConfig.find(({ field }) => field === fieldName) || null;
    },
    getValueForPublish(requiredValueForPublish) {
      if (isObject(requiredValueForPublish)) {
        return requiredValueForPublish.value;
      }
      return requiredValueForPublish;
    },
    onVisibleScope(scopeName, index) {
      if (!this.scopesOnViewport.includes(scopeName)) {
        this.scopesOnViewport.push(scopeName);
      }
      this.scopesOnViewport.sort((a, b) => this.scopesToBeShown.indexOf(a) - this.scopesToBeShown.indexOf(b));
      this.visibleScope = this.scopesOnViewport[0];
    },
    onInvisibleScope(scopeName, index) {
      const position = this.scopesOnViewport.indexOf(scopeName);
      if (position > -1) {
        this.scopesOnViewport.splice(position, 1);
      }
    },
  },
  async mounted() {
    this.configureAffixTabs();

    // By default we're always in basic mode, so we toggle if the config says we're in advanced
    if (this.getConfig().advancedMode || !this.basicAdvancedEnabled) {
      await this.dispatch('ENABLE_ADVANCED_EDITOR');
    }

    this.updateScopesToBeShown();
  },
};
</script>
